import { getLocalPhoneNumberIfIsraeliNumber } from "utils/Haim/phoneNumbersUtils";
import { ProcessedListingData, User } from "./../../@types/sharedSchema";
import { numberToCurrency } from "utils/listings/display";
import i18n from "../../locales/i18n";

// Types moved from types.ts
export type promptGeneratorInitialParams = {
  realtorData: Pick<User, "ownerFullName" | "ownerPhoneNumber" | "userImageUrl" | "companyLogoUrl">;
  selectedListings: { [inputName: string]: ProcessedListingData | null };
};

export type ListingImageAdditionalData = {
  imageSourceHint?: "listing_images";
  listingNameInPrompt?: string;
};

export type colorNameAndValue = {
  name: string;
  value: string;
  preview: string;
};

export type ColorAdditionalData = {
  predefinedColors?: Array<colorNameAndValue>;
  allowCustomText?: boolean;
  customTextPlaceholder?: string;
};

export type templateInput = {
  label: string | ReturnType<typeof i18n.t>;
  type: "image" | "text" | "listing" | "color" | "boolean";
  nameInPrompt?: string;
  showInSection: "template_specific" | "realtor_info" | "listing_info";
  description: string | ReturnType<typeof i18n.t>;
  additionalData?: ListingImageAdditionalData | ColorAdditionalData;
  defaultValue?: (
    params: promptGeneratorInitialParams
  ) => string | boolean | ReturnType<typeof i18n.t> | undefined;
  required?: boolean;
};

export type promptGenerationFinalParams = {
  promptGeneratorInitialParams: promptGeneratorInitialParams;
  realtorImageAlignement?: "left" | "right";
  templateFinalInputs: (templateInput & {
    finalValue: string | boolean | ReturnType<typeof i18n.t>;
  })[];
};

export type templateDataType = {
  templateName: string;
  displayImageUrl: string;
  description: string;
  inputs: templateInput[];
  promptGenerator: (params: promptGenerationFinalParams) => string;
};

// Prompt helper functions moved from prompt_helpers.ts
export function getBsadPrompt(
  params: promptGenerationFinalParams,
  asProperty: boolean = true
): string {
  const includeBsad =
    params.templateFinalInputs.find((input) => input.nameInPrompt === "include_bsad")
      ?.finalValue === true;

  if (includeBsad) {
    if (asProperty) {
      return '"top_right_overlay": {"text": "בס״ד", "position": "top-right corner", "style": "small, elegant Hebrew text"},';
    }
    return ',\n      "top_right_small_text": "בס״ד"';
  }

  return "";
}

export function getRealtorName(params: promptGenerationFinalParams): string {
  return params.templateFinalInputs.find((input) => input.nameInPrompt === "realtor_name")
    ?.finalValue as string;
}

export function getRealtorPhoneNumber(params: promptGenerationFinalParams): string {
  return params.templateFinalInputs.find((input) => input.nameInPrompt === "realtor_phone_number")
    ?.finalValue as string;
}

export function getRealtorContactInfo(params: promptGenerationFinalParams): string {
  return params.templateFinalInputs.find((input) => input.nameInPrompt === "realtor_contact_info")
    ?.finalValue as string;
}

export function getFooterPrompt(params: promptGenerationFinalParams): string {
  const realtorImageAlignment = params.realtorImageAlignement ?? "left";
  return `"footer": {
        "background_color": "#ffffff",
        "content": [
          {
            "type": "contact_info",
            "realtor_name": "${getRealtorName(params)}",
            "phone_number": "${getRealtorPhoneNumber(params)}",
            "position": "${realtorImageAlignment}"
          },
          {
            "type": "logo",
            "source": "realtor_logo",
            "position": "right",
            "description": "Logo"
          }
        ]
      }`;
}

export function getVerticalFooterPrompt(
  params: promptGenerationFinalParams,
  additionalContentPrompt?: string
): string {
  const realtorImageAlignment = params.realtorImageAlignement ?? "left";
  return `"footer_section",
        "height": "12%",
        "background_color": "white",
        "content": [
          {
            "type": "contact_info",
            "realtor_name": "${getRealtorName(params)}",
            "phone_number": "${getRealtorPhoneNumber(params)}",
            "position": "${realtorImageAlignment === "left" ? "right" : "left"}",
            "style": "professional font, dark color"
          },
          {
            "type": "logo",
            "source": "realtor_logo",
             "instructions": "Place the cut-out (background removed) image of the logo in the bottom ${
               realtorImageAlignment === "left" ? "right" : "left"
             } corner.", 
            "position": "opposite_to_contact",
            "style": "professional placement"
          }
          ${additionalContentPrompt ? `, ${additionalContentPrompt}` : ""}
        ]`;
}

export const INCLUDE_REALTOR_IMAGE_INPUT_NAME = "include_realtor_image";

export function hasRealtorImage(params: promptGenerationFinalParams): boolean {
  const includeToggle = params.templateFinalInputs.find(
    (input) => input.nameInPrompt === INCLUDE_REALTOR_IMAGE_INPUT_NAME
  );
  // Default to including the realtor image when the toggle is not present (back-compat).
  if (includeToggle && includeToggle.finalValue === false) return false;

  const realtorImageValue = params.templateFinalInputs.find(
    (input) => input.nameInPrompt === "realtor_image"
  )?.finalValue;
  return typeof realtorImageValue === "string" && realtorImageValue.trim() !== "";
}

export function getRealtorImagePrompt(
  params: promptGenerationFinalParams,
  additionalInstructions?: string
): string {
  if (!hasRealtorImage(params)) return "";

  const realtorImageAlignment = params.realtorImageAlignement ?? "left";
  const baseInstructions = `Place the cut-out (background removed) image of the realtor in the bottom ${realtorImageAlignment} corner.`;
  const finalInstructions = `${baseInstructions} ${additionalInstructions ?? ""}`;
  return `"subject_layer": {
        "source": "realtor_image",
        "size": "about 30% of the width of the flyer",
        "position": "bottom_${realtorImageAlignment}",
        "style": "cut-out image"
        "instructions": "${finalInstructions}"
    }`;
}

// Helper function to extract color information from template inputs
export function getColorInfo(params: promptGenerationFinalParams): {
  mainColorValue: string;
  mainColorName: string;
  lighterColorValue: string;
  darkerColorValue: string;
} {
  const mainColorValue = (params.templateFinalInputs.find(
    (input) => input.nameInPrompt === "main_color"
  )?.finalValue || defaultColor.value) as string;

  // Find the color name from predefined colors, fallback to the value itself
  const colorInput = params.templateFinalInputs.find(
    (input) => input.nameInPrompt === "main_color"
  );
  const predefinedColors =
    (colorInput?.additionalData as ColorAdditionalData)?.predefinedColors || [];
  const selectedColorObj = predefinedColors.find((color) => color.value === mainColorValue);
  const mainColorName = selectedColorObj ? selectedColorObj.name : mainColorValue;

  return {
    mainColorValue: mainColorValue,
    mainColorName: mainColorName,
    lighterColorValue: `lighter shade of ${mainColorValue}`,
    darkerColorValue: `darker shade of ${mainColorValue}`
  };
}

export function getVisualStylePrompt(params: promptGenerationFinalParams): string {
  const { mainColorName: colorName } = getColorInfo(params);
  const gradientColor = `${colorName} gradient`;

  return `"visual_style": {
  "font_family": "Modern, bold sans-serif, suitable for Hebrew text.",
    "color_palette": {
      "primary": "${colorName}",
      "secondary": "vibrant ${gradientColor}",
      "accents": "White, Black"
    },
    "lighting": "High-key, with lens flare effects emphasizing a bright future",
    "saturation": "High saturation to create an appealing, hyper-real look"
  }`;
}

/**
 * Appended to every creative prompt that carries the realtor's logo. gpt-image-2 (since 2026-09-17)
 * re-draws its input images, and the prompts only name images by role ("the realtor logo") while the
 * model receives a bare list. Without this it re-drew logos loosely, recoloured a gold logo card blue,
 * and completed a text-less logo with an invented company name (11 of 12 runs of one realtor's
 * requests; 0 of 12 with this). Measured with webot-server's
 * adHocOperations/audit-creative-fidelity.ts, which holds a word-for-word copy — change both.
 * `logoImageNumber` is the logo's 1-based position in the request's imageUrls (it varies by template
 * and by which optional images are filled in, from 1 to 4 in production — never hardcode it).
 */
export function getRealtorLogoPrompt(logoImageNumber: number): string {
  const n = logoImageNumber;
  return `

REALTOR LOGO — follow exactly, this overrides any other logo instruction above:
Input image ${n} is the realtor's company logo, a finished brand asset. Place it in the design exactly as it appears in image ${n}: the same shapes, colours, letters, proportions and spacing, only scaled to fit. Copy it — do not redraw, restyle, recolour, simplify or "improve" it.
A plain white or transparent background around the logo is not part of it: place the logo without it, and never turn it into a black or coloured box. A coloured card or tile the logo is printed on IS part of it: keep it.
Add nothing to the logo: no company name, brand name, wordmark, slogan or tagline that is not already inside image ${n}. If the logo has no text, it has no text in the design either. Do not remove any part of it.`;
}

/** "Fix logo" on an existing creative: image 1 is the creative, the logo is image `logoImageNumber`. */
export function getFixRealtorLogoPrompt(logoImageNumber: number): string {
  return (
    `Replace the realtor's logo in image 1 (if any) — together with any company name or slogan drawn next to it that is not part of the real logo — with the logo from image ${logoImageNumber}. Change nothing else in image 1.` +
    getRealtorLogoPrompt(logoImageNumber)
  );
}

// Return a descriptive label for an image input by its nameInPrompt
const imageLabels: Record<string, string> = {
  listing_image: "the first listing image",
  listing_image_2: "the second listing image",
  listing_image_3: "the third listing image",
  listing_image_4: "the fourth listing image",
  second_listing_image: "the second listing image",
  realtor_image: "the realtor image",
  realtor_logo: "the realtor logo"
};

export function getInputImageLabel(nameInPrompt: string): string {
  return imageLabels[nameInPrompt] || nameInPrompt;
}

// Reusable template sections for creatives generator

export const realtorContactInfoTemplateInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.realtorContactInfo"),
  type: "text",
  nameInPrompt: "realtor_contact_info",
  showInSection: "realtor_info",
  description: i18n.t("creativesGenerator.templates.realtorContactInfoDescription"),
  defaultValue: (params: promptGeneratorInitialParams) => {
    const name = params.realtorData.ownerFullName ?? "_____";
    let localizedPhone: string;

    try {
      localizedPhone =
        getLocalPhoneNumberIfIsraeliNumber(params.realtorData.ownerPhoneNumber, false) ?? "_____";
    } catch {
      localizedPhone = "_____";
    }
    return `${name} | ${localizedPhone}`;
  }
};

export const realtorNameTemplateInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.realtorName"),
  type: "text",
  nameInPrompt: "realtor_name",
  showInSection: "realtor_info",
  description: i18n.t("creativesGenerator.templates.realtorNameDescription"),
  defaultValue: (params: promptGeneratorInitialParams) => {
    return params.realtorData.ownerFullName ?? "_____";
  }
};

export const realtorPhoneNumberTemplateInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.realtorPhoneNumber"),
  type: "text",
  nameInPrompt: "realtor_phone_number",
  showInSection: "realtor_info",
  description: i18n.t("creativesGenerator.templates.realtorPhoneNumberDescription"),
  defaultValue: (params: promptGeneratorInitialParams) => {
    let localizedPhone: string;

    try {
      localizedPhone =
        getLocalPhoneNumberIfIsraeliNumber(params.realtorData.ownerPhoneNumber, false) ?? "_____";
    } catch {
      localizedPhone = "_____";
    }
    return localizedPhone;
  }
};

// Listing input for selecting a property
export const mainListingInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.mainListing"),
  type: "listing",
  nameInPrompt: "main_listing",
  showInSection: "listing_info",
  description: i18n.t("creativesGenerator.templates.mainListingDescription")
};

export const firstImageOfListingTemplateInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.houseImage"),
  type: "image",
  nameInPrompt: "listing_image",
  showInSection: "listing_info",
  description: i18n.t("creativesGenerator.templates.houseImageDescription"),
  additionalData: {
    imageSourceHint: "listing_images",
    listingNameInPrompt: "main_listing"
  },
  defaultValue: (params: promptGeneratorInitialParams) => {
    const mainListing = params.selectedListings["main_listing"];
    return mainListing?.imageUrls?.length! > 0 ? mainListing?.imageUrls?.[0] : "";
  }
};

export const secondImageOfListingTemplateInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.houseImage2"),
  type: "image",
  nameInPrompt: "listing_image_2",
  showInSection: "listing_info",
  description: i18n.t("creativesGenerator.templates.houseImage2Description"),
  additionalData: {
    imageSourceHint: "listing_images",
    listingNameInPrompt: "main_listing"
  },
  defaultValue: (params: promptGeneratorInitialParams) => {
    const mainListing = params.selectedListings["main_listing"];
    return mainListing?.imageUrls?.length! > 1 ? mainListing?.imageUrls?.[1] : "";
  }
};

export const thirdImageOfListingTemplateInput = (required = false): templateInput => ({
  label: i18n.t("creativesGenerator.templates.houseImage3"),
  type: "image",
  nameInPrompt: "listing_image_3",
  showInSection: "listing_info",
  description: i18n.t("creativesGenerator.templates.houseImage3Description"),
  required: required,
  additionalData: {
    imageSourceHint: "listing_images",
    listingNameInPrompt: "main_listing"
  },
  defaultValue: (params: promptGeneratorInitialParams) => {
    const mainListing = params.selectedListings["main_listing"];
    return mainListing?.imageUrls?.length! > 2 ? mainListing?.imageUrls?.[2] : "";
  }
});

export const fourthImageOfListingTemplateInput = (required = false): templateInput => ({
  label: i18n.t("creativesGenerator.templates.houseImage4"),
  type: "image",
  nameInPrompt: "listing_image_4",
  showInSection: "listing_info",
  description: i18n.t("creativesGenerator.templates.houseImage4Description"),
  required: false,
  additionalData: {
    imageSourceHint: "listing_images",
    listingNameInPrompt: "main_listing"
  },
  defaultValue: (params: promptGeneratorInitialParams) => {
    const mainListing = params.selectedListings["main_listing"];
    return mainListing?.imageUrls?.length! > 3 ? mainListing?.imageUrls?.[3] : "";
  }
});

export const realtorLogoTemplateInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.realtorLogo"),
  type: "image",
  nameInPrompt: "realtor_logo",
  showInSection: "realtor_info",
  description: i18n.t("creativesGenerator.templates.realtorLogoDescription"),
  defaultValue: (params: promptGeneratorInitialParams) => params.realtorData.companyLogoUrl
};

export const realtorImageTemplateInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.realtorImage"),
  type: "image",
  nameInPrompt: "realtor_image",
  showInSection: "realtor_info",
  description: i18n.t("creativesGenerator.templates.realtorImageDescription"),
  required: false,
  defaultValue: (params: promptGeneratorInitialParams) => params.realtorData.userImageUrl
};

export const includeRealtorImageInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.includeRealtorImage"),
  type: "boolean",
  nameInPrompt: INCLUDE_REALTOR_IMAGE_INPUT_NAME,
  showInSection: "realtor_info",
  description: i18n.t("creativesGenerator.templates.includeRealtorImageDescription"),
  defaultValue: () => true
};

export const listingPrice: templateInput = {
  label: i18n.t("creativesGenerator.templates.listingPrice"),
  type: "text",
  nameInPrompt: "listing_price",
  showInSection: "listing_info",
  description: i18n.t("creativesGenerator.templates.listingPriceDescription"),
  defaultValue: (params: promptGeneratorInitialParams) => {
    const mainListing = params.selectedListings["main_listing"];
    return mainListing?.price ? numberToCurrency(mainListing.price) : "_____ ₪";
  }
};

export const listingPriceOptional: templateInput = {
  ...listingPrice,
  required: false,
  description: i18n.t("creativesGenerator.templates.listingPriceOptionalDescription")
};

export const defaultColor: colorNameAndValue = {
  name: "Forest Green",
  value: "#4caf50",
  preview: "#4caf50"
};
export const includeBsadInput: templateInput = {
  label: "כולל בס״ד",
  type: "boolean",
  nameInPrompt: "include_bsad",
  showInSection: "template_specific",
  description: "הוספת הטקסט 'בס״ד' בפינה הימנית העליונה של הקריאטיב",
  defaultValue: () => true
};

export const mainTemplateColorInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.mainColor"),
  type: "color",
  nameInPrompt: "main_color",
  showInSection: "template_specific",
  description: i18n.t("creativesGenerator.templates.mainColorDescription"),
  defaultValue: () => defaultColor.value,
  additionalData: {
    predefinedColors: [
      defaultColor,
      { name: "Ocean Blue", value: "#2196f3", preview: "#2196f3" },
      { name: "Sunset Orange", value: "#ff9800", preview: "#ff9800" },
      { name: "Royal Purple", value: "#9c27b0", preview: "#9c27b0" },
      { name: "Crimson Red", value: "#f44336", preview: "#f44336" },
      { name: "Golden Yellow", value: "#ffc107", preview: "#ffc107" },
      { name: "Teal", value: "#009688", preview: "#009688" },
      { name: "Deep Pink", value: "#e91e63", preview: "#e91e63" },
      { name: "Indigo", value: "#3f51b5", preview: "#3f51b5" },
      { name: "Brown", value: "#795548", preview: "#795548" },
      { name: "Slate Gray", value: "#607d8b", preview: "#607d8b" },
      { name: "Lime Green", value: "#8bc34a", preview: "#8bc34a" }
    ],
    allowCustomText: true,
    customTextPlaceholder: "e.g., 'vibrant blue', 'warm red', 'gold', '#ff5733'"
  }
};
